/* CUSTOM_ECORE_BY_CLAUDE — universal sample picker.
   On submit: posts a single line of the $0 sample SKU to /cart/add.js with
   line-item properties for every selection + the 3 questions, then
   redirects to /checkout. Quantity = 1 so the cart shows one line item;
   selections are listed inside the property value. */

(function () {
  'use strict';

  function SamplePicker(section) {
    this.section = section;
    this.variantId = parseInt(section.getAttribute('data-sample-variant-id'), 10);
    this.defaultUseCase = section.getAttribute('data-default-use-case') || '';
    this.selections = []; // [{productTitle, variantTitle, button}]
    this.submitting = false; // double-submit guard

    var urlVertical = '';
    try {
      var params = new URLSearchParams(window.location.search);
      urlVertical = params.get('vertical') || '';
    } catch (e) { /* graceful fall-back if URLSearchParams unavailable */ }

    if (urlVertical && !this.defaultUseCase) {
      this.applyUrlVertical(urlVertical);
    }

    this.bind();
    this.bindDragScroll();
    this.bindPageshowReset();
    this.restoreAnswers();
    this.applyDefaultFilter();
    this.refreshUi();
    this.detectVariantOverflow();
    this.markCartedSwatches();
    this.applyFocusFromUrl();

    var self = this;
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { self.detectVariantOverflow(); });
      this.section.querySelectorAll('.ecore-samples__variants-wrap').forEach(function (w) {
        ro.observe(w);
      });
    } else {
      window.addEventListener('resize', function () { self.detectVariantOverflow(); });
    }
  }

  // Click-and-drag horizontal scrolling on each variant strip. Mobile
  // already gets native touch-swipe via overflow-x:auto. On desktop, mouse
  // users were stuck with the 6px scrollbar — couldn't drag the swatches
  // themselves to pan. This adds mouse-only drag-to-scroll. Distinguishes
  // a drag (>5px movement) from a click (<=5px); a real drag suppresses
  // the trailing click so the swatch isn't accidentally selected.
  // ?focus=<product-handle> entry-point support: when a user lands on
  // the samples page from a specific product page, that page can pass the
  // product handle in the URL. We then:
  //   1. Add .is-spotlighted on the matching <li> (CSS gives it a thicker
  //      accent border + corner badge so the user's eye lands on it).
  //   2. Show the sticky focus banner with the product title + a
  //      "Jump to it ↓" button that smooth-scrolls to the card.
  // No-op if the param is missing or doesn't match a rendered card.
  SamplePicker.prototype.applyFocusFromUrl = function () {
    var self = this;
    var focus;
    try {
      focus = new URLSearchParams(window.location.search).get('focus');
    } catch (e) { return; }
    if (!focus) return;

    var card = this.section.querySelector('.ecore-samples__product[data-handle="' + focus.replace(/"/g, '\\"') + '"]');
    if (!card) return;

    card.classList.add('is-spotlighted');
    /* CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - held on the instance so `retireFocus` can
       reach them from the click handler. The observer especially: without a handle on it there
       is no way to STOP it, and a cue that has been faded out would be un-hidden by the next
       scroll callback. */
    this.focusCard = card;
    this.focusBanner = null;
    this.focusIo = null;

    var banner = this.section.querySelector('[data-sample-focus-banner]');
    if (!banner) return;

    var titleEl = banner.querySelector('[data-focus-product-title]');
    if (titleEl) titleEl.textContent = card.getAttribute('data-product-title') || 'this product';
    var jumpBtn = banner.querySelector('[data-sample-focus-jump]');
    if (jumpBtn) {
      jumpBtn.addEventListener('click', function () {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - the cue is for a card you CANNOT SEE.
    //
    // Ryan: *"if your product is down a little bit (meaning it's not on the visible row on
    // page load for desktop or mobile), I want a bouncing label/arrow type thing at the
    // bottom."* It used to show whenever a focus handle resolved, so a card sitting in the
    // first row got a banner telling the reader to jump to something already under their eyes.
    //
    // ⛔ THE CARD'S OWN POSITION ANSWERS IT, not a fold-height guess. An
    // IntersectionObserver keeps the cue in step for the whole session - it comes back if the
    // reader scrolls past and away again, which a one-shot check on load could not do.
    //
    // ⛔ "MEANINGFULLY ON SCREEN" IS `intersectionRatio`, NOT `isIntersecting`. This comment
    // used to credit the 0.35 threshold with that job and the code asked `isIntersecting`,
    // which is true at ratio > 0 REGARDLESS of any threshold - so a card one pixel into view
    // dismissed the cue pointing at it. A threshold decides WHEN the callback runs; it never
    // decides what `isIntersecting` means.
    //
    // ⚠️ NO OBSERVER, ALWAYS SHOW. On a browser without IntersectionObserver the cue is a
    // harmless extra button; hiding it instead would remove the only pointer to the card.
    /* CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - ⛔ THE CUE TURNS AROUND WHEN YOU SCROLL PAST.
       *"if you scroll past the swatch that you arrived from, the bottom popup appears again
       telling you to scroll down... at this point, it should change to Scroll up with an up
       arrow."* It reappeared correctly - it just kept pointing the wrong way, which sends the
       reader further from the thing they came for.

       ⛔ FIRST CUT SHIPPED BROKEN AND THE TEST AGREED WITH IT (fixed 2026-08-25). It asked
       `boundingClientRect.bottom <= rootBounds.top` - "is the card ENTIRELY above the
       viewport". That is never true at the moment this callback runs: the observer fires on a
       THRESHOLD CROSSING, so at 0.35 roughly a third of the card is still on screen and its
       bottom edge is still below the fold. And once the card IS entirely above, no further
       callback fires - the ratio stays 0, so nothing crosses. The cue therefore said "down"
       for ever, which is exactly what Ryan saw.
       ⚠️ The jsdom harness passed because it fed geometry for a card FULLY above - my own
       assumption handed back to me. It now feeds the geometry of the firing MOMENT.

       ✅ SO THE SIDE IS THE MIDPOINT COMPARISON, which holds at any firing moment: if the
       card's centre is above the viewport's centre, the reader has gone past it.
       ⚠️ `rootBounds` IS NULL in some cross-origin/hidden-frame cases, so it falls back to the
       window box - which is what rootBounds is for a document-root observer anyway. */
    /* CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - does the line need to scroll?
       ⚠️ MEASURED WHILE VISIBLE, ALWAYS. A hidden element reports zero widths, so calling this
       before the cue is shown decides "it fits" for every product name there is. Every caller
       runs after `hidden` is cleared.
       ⚠️ Cleared before re-measuring: leaving the old `--esp-marquee` in place would size the
       next name by the previous one, and the class itself widens nothing but changes
       `text-overflow`, which is enough to shift the measurement. */
    function syncMarquee() {
      var win = banner.querySelector('.ecore-samples__focus-text');
      var line = banner.querySelector('.ecore-samples__focus-scroll');
      if (!win || !line) return;
      win.classList.remove('is-marquee');
      win.style.removeProperty('--esp-marquee');
      var over = line.scrollWidth - win.clientWidth;
      /* A couple of px of slop is rounding, not overflow - animating that is a twitch. */
      if (over > 6) {
        win.style.setProperty('--esp-marquee', over + 'px');
        win.classList.add('is-marquee');
      }
    }

    function setCueDirection(isAbove) {
      var word = banner.querySelector('[data-focus-direction]');
      var arrow = banner.querySelector('[data-focus-arrow]');
      if (word) word.textContent = isAbove ? 'up' : 'down';
      if (arrow) arrow.textContent = isAbove ? '\u2191' : '\u2193';
      banner.classList.toggle('is-up', !!isAbove);
    }

    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var en = entries[i];
          var bcr = en.boundingClientRect;
          var rb = en.rootBounds;
          var rootTop = rb ? rb.top : 0;
          var rootBottom = rb ? rb.bottom : (window.innerHeight || 0);

          /* ⚠️ A CARD TALLER THAN A THIRD OF THE VIEWPORT can never reach ratio 0.35 even
             while it fills the screen, so "how much of the CARD is showing" is asked alongside
             "how much of the SCREEN it fills". Either one counts as being there. */
          var shownH = en.intersectionRect ? (en.intersectionRect.bottom - en.intersectionRect.top) : 0;
          var rootH = (rootBottom - rootTop) || 1;
          var visible = en.intersectionRatio >= 0.35 || (shownH / rootH) >= 0.35;

          /* ⚠️ DIRECTION FIRST, THEN VISIBILITY. Unhiding before the word is rewritten shows
             one frame of the previous direction - which on a fast scroll past the card is a
             cue that briefly points the wrong way at the exact moment it is being read. */
          if (!visible) {
            setCueDirection(((bcr.top + bcr.bottom) / 2) < ((rootTop + rootBottom) / 2));
          }
          banner.hidden = visible;
          /* ⚠️ AFTER unhiding, never before - see syncMarquee. The direction word changes
             width ("up" vs "down"), so this is re-asked on every appearance rather than once. */
          if (!visible) syncMarquee();
        }
      /* ⚠️ TWO THRESHOLDS. 0.35 is the show/hide boundary; 0 gives a second callback as the
         card finally clears the viewport, so the direction is re-read with the card unambiguously
         on one side rather than only at the moment it began to leave. */
      }, { threshold: [0, 0.35] });
      io.observe(card);
      this.focusIo = io;
    } else {
      banner.hidden = false;
    }
    this.focusBanner = banner;

    /* ⚠️ A rotate changes the window width without any scrolling, so the observer never
       fires and the measurement would stay sized for the old orientation. Debounced, and it
       does nothing while the cue is hidden (zero widths - see syncMarquee). */
    var mqTimer = null;
    window.addEventListener('resize', function () {
      if (mqTimer) clearTimeout(mqTimer);
      mqTimer = setTimeout(function () { if (!banner.hidden) syncMarquee(); }, 180);
    });
  };

  /* ============================================
     retireFocus - the cue has done its job
     ============================================
     CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - *"as soon as a single swatch of the pulsing
     product is added, it should stop pulsing/being highlighted (smooth fade back to normal)
     and the bottom banner should also fade away, not to return."*

     ⛔ THE OBSERVER IS DISCONNECTED FIRST, BEFORE ANYTHING FADES. It is the only thing that
     can set `banner.hidden = false`, and it fires on scroll - so fading the cue while it is
     still watching means the next scroll snaps it back mid-fade. "Not to return" is a
     property of the observer being gone, not of the opacity reaching zero.

     ⚠️ ONE SWATCH IS ENOUGH, and only on a real ADD. The first click on a swatch is a
     preview; this hangs off the second, which is the click that actually puts something in
     the request. Removing it again does NOT bring the spotlight back - Ryan asked for "not to
     return", and a highlight that reappears when you change your mind is the same nag twice.

     ⚠️ THE CLASSES COME OFF AFTER THE TRANSITION, not with it. Removing `is-spotlighted`
     immediately would drop the accent border and the badge in one frame, which is the snap the
     fade exists to avoid; `is-spotlight-done` is what animates, and both leave together once
     it has finished. */
  SamplePicker.prototype.retireFocus = function (productEl) {
    if (!this.focusCard || !productEl || productEl !== this.focusCard) return;

    if (this.focusIo) {
      try { this.focusIo.disconnect(); } catch (e) { /* already gone */ }
      this.focusIo = null;
    }

    var banner = this.focusBanner;
    if (banner) {
      banner.classList.add('is-dismissed');
      setTimeout(function () {
        banner.hidden = true;
        banner.classList.remove('is-dismissed');
      }, 420);
    }

    var el = this.focusCard;
    el.classList.add('is-spotlight-done');
    setTimeout(function () {
      el.classList.remove('is-spotlighted');
      el.classList.remove('is-spotlight-done');
    }, 560);

    this.focusCard = null;
    this.focusBanner = null;
  };

  // Persist + restore questionnaire answers in localStorage so the user
  // doesn't have to re-pick Project Type / Square Feet / Role / etc. when
  // they navigate away and come back to add more samples. Keyed by a fixed
  // string since there's only one sample picker per site; if that ever
  // changes, switch to a section-id-based key.
  SamplePicker.prototype.STORAGE_KEY = 'bsf-sample-picker-answers';

  SamplePicker.prototype.persistAnswers = function () {
    try {
      var data = {};
      this.section.querySelectorAll('[data-sample-q]').forEach(function (el) {
        var key = el.getAttribute('data-sample-q');
        if (key && el.value) data[key] = el.value;
      });
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* localStorage may be disabled — fail silent */ }
  };

  SamplePicker.prototype.restoreAnswers = function () {
    var self = this;
    try {
      var raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      Object.keys(data).forEach(function (key) {
        var el = self.section.querySelector('[data-sample-q="' + key.replace(/"/g, '\\"') + '"]');
        if (!el || !data[key]) return;
        el.value = data[key];
        // Fire change so dependent UI (Other input visibility, etc.) syncs.
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (e) { /* corrupt JSON or storage error — start fresh */ }
  };

  // Reset submit button state when returning to the page via the browser's
  // back-forward cache. Without this, a user who clicked "Continue to
  // Checkout" then hit Back to add more samples sees a stuck disabled
  // button with "Adding..." label — submit() bailed early because
  // submitting=true was preserved in the BFCache snapshot.
  // Also re-syncs cart-state markers in case the cart changed elsewhere.
  SamplePicker.prototype.bindPageshowReset = function () {
    var self = this;
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        self.resetSubmitState();
        self.markCartedSwatches();
      }
    });
  };

  // Fetch the current cart and:
  //  - Mark each swatch button whose Sample property already lives in the
  //    cart with data-in-cart="true" so the user gets a green ✓ visual
  //    cue and doesn't waste clicks re-adding the same sample.
  //  - Track this.cartHasSamples so Continue to Checkout can be enabled
  //    even with an empty sidebar (the user has prior samples to check
  //    out without adding anything new).
  SamplePicker.prototype.markCartedSwatches = function () {
    var self = this;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sampleItems = (cart.items || [])
          .filter(function (item) { return item.variant_id === self.variantId; });
        self.cartHasSamples = sampleItems.length > 0;
        self.cartSampleCount = sampleItems.length;

        // Cart-as-source-of-truth for "in-flight request":
        // - Cart has samples → continuation; keep persisted answers so
        //   user adding more samples doesn't re-fill the questionnaire.
        // - Cart is empty → user either just checked out (clean slate
        //   desired) or never added anything; clear stale answers from
        //   localStorage AND blank the form fields. Protects shared-
        //   computer privacy without punishing the active user.
        if (sampleItems.length === 0) {
          try { localStorage.removeItem(self.STORAGE_KEY); } catch (e) {}
          self.section.querySelectorAll('[data-sample-q]').forEach(function (el) {
            if (el.value) {
              el.value = '';
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        // Clear stale in-cart markers first (cart may have shrunk).
        self.section.querySelectorAll('[data-sample-variant][data-in-cart="true"]').forEach(function (b) {
          b.removeAttribute('data-in-cart');
        });

        // Also drop any selections that came from a previous cart fetch but
        // are no longer in the cart (e.g. user removed them in the cart UI).
        self.selections = self.selections.filter(function (s) {
          if (!s.inCart) return true;
          return sampleItems.some(function (it) {
            return it.properties && it.properties.Sample === self.sampleLabelFor(s.productTitle, s.variantTitle);
          });
        });

        // Mark swatch buttons + hydrate sidebar with anything in the cart
        // that isn't already shown.
        sampleItems.forEach(function (item) {
          var label = item.properties && item.properties.Sample;
          if (!label) return;
          // Parse 'Product Title — Variant Title' (split on first ' — ' so
          // product titles containing ' — ' would still parse, since variant
          // titles are appended last).
          var sepIdx = label.indexOf(' — ');
          var productTitle = sepIdx === -1 ? label : label.slice(0, sepIdx);
          var variantTitle = sepIdx === -1 ? 'Default Title' : label.slice(sepIdx + 3);

          // Find the matching swatch button.
          var btn = null;
          self.section.querySelectorAll('[data-sample-variant]').forEach(function (b) {
            if (b.getAttribute('data-product-title') === productTitle &&
                b.getAttribute('data-variant-title') === variantTitle) btn = b;
          });
          if (btn) {
            btn.setAttribute('data-in-cart', 'true');
            btn.setAttribute('aria-pressed', 'true');
          }

          // Push into selections list if not already there.
          var exists = self.selections.some(function (s) {
            return s.productTitle === productTitle && s.variantTitle === variantTitle;
          });
          if (!exists) {
            self.selections.push({
              productTitle: productTitle,
              variantTitle: variantTitle,
              button: btn,
              inCart: true,
              cartItemKey: item.key
            });
          } else {
            // Already in selections — just stamp the cart key on the entry
            // so removeAt() can remove from cart too.
            self.selections.forEach(function (s) {
              if (s.productTitle === productTitle && s.variantTitle === variantTitle) {
                s.inCart = true;
                s.cartItemKey = item.key;
              }
            });
          }
        });
        self.refreshUi();
      })
      .catch(function () { /* cart fetch failure shouldn't break the picker */ });
  };

  SamplePicker.prototype.sampleLabelFor = function (productTitle, variantTitle) {
    var label = productTitle;
    if (variantTitle && variantTitle !== 'Default Title') label += ' — ' + variantTitle;
    return label;
  };

  SamplePicker.prototype.bindDragScroll = function () {
    var DRAG_THRESHOLD = 5;
    var section = this.section;
    // Single shared drag-state for this picker instance. ONE pair of
    // document-level mousemove/mouseup listeners services all variant
    // strips; previously each strip added its own pair (60+ listeners on
    // a 30-product page). The active list is set on mousedown.
    var activeList = null;
    var startX = 0, startScroll = 0, totalMove = 0, draggingApplied = false;
    var originSwatch = null;

    this.section.querySelectorAll('.ecore-samples__variants').forEach(function (list) {
      list.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        activeList = list;
        startX = e.clientX;
        startScroll = list.scrollLeft;
        totalMove = 0;
        draggingApplied = false;
        originSwatch = e.target.closest('.ecore-samples__variant');
        list.classList.add('is-grabbing');
      });
    });

    document.addEventListener('mousemove', function (e) {
      if (!activeList) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > totalMove) totalMove = Math.abs(dx);
      // 2x multiplier matches the site-wide slider drag pattern in main.js
      // (line ~1804) — feels snappier than 1:1, less wrist travel needed.
      activeList.scrollLeft = startScroll - dx * 2;
      // Once we've crossed the click-vs-drag threshold, mark the section
      // as actively dragging so CSS can suppress :hover outlines on
      // swatches the cursor sweeps past mid-drag.
      if (!draggingApplied && totalMove > DRAG_THRESHOLD) {
        section.classList.add('is-swatch-dragging');
        if (originSwatch) originSwatch.classList.add('is-drag-origin');
        draggingApplied = true;
      }
      e.preventDefault();
    });

    document.addEventListener('mouseup', function () {
      if (!activeList) return;
      activeList.classList.remove('is-grabbing');
      section.classList.remove('is-swatch-dragging');
      if (originSwatch) {
        originSwatch.classList.remove('is-drag-origin');
        originSwatch = null;
      }
      if (totalMove > DRAG_THRESHOLD) {
        // Suppress the imminent click so dragging doesn't toggle a swatch.
        var suppress = function (clickEvent) {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          window.removeEventListener('click', suppress, true);
        };
        window.addEventListener('click', suppress, true);
        // Belt-and-suspenders: clear listener if no click event materializes.
        setTimeout(function () { window.removeEventListener('click', suppress, true); }, 0);
      }
      activeList = null;
    });
  };

  // Set data-has-overflow="true" on each variant wrap whose inner list
  // overflows horizontally, so the right-edge fade gradient only shows when
  // there's actually content to scroll to. Also wire each list's scroll
  // event to toggle data-at-end="true" when the user reaches the right
  // edge — at that point the fade is misleading and CSS hides it.
  SamplePicker.prototype.detectVariantOverflow = function () {
    this.section.querySelectorAll('.ecore-samples__variants-wrap').forEach(function (wrap) {
      var inner = wrap.querySelector('.ecore-samples__variants');
      if (!inner) return;
      var overflows = inner.scrollWidth > inner.clientWidth + 1;
      wrap.setAttribute('data-has-overflow', overflows ? 'true' : 'false');

      // Compute + set the at-end flag immediately, then wire scroll handler.
      var setAtEnd = function () {
        var atEnd = inner.scrollLeft >= (inner.scrollWidth - inner.clientWidth - 1);
        wrap.setAttribute('data-at-end', atEnd ? 'true' : 'false');
      };
      setAtEnd();
      if (!inner.__atEndBound) {
        inner.__atEndBound = true;
        inner.addEventListener('scroll', setAtEnd, { passive: true });
      }
    });
  };

  SamplePicker.prototype.applyUrlVertical = function (verticalTag) {
    var needle = String(verticalTag).toLowerCase().trim();
    if (!needle) return;
    var products = this.section.querySelectorAll('.ecore-samples__product');
    products.forEach(function (li) {
      var raw = li.getAttribute('data-tags') || '';
      // Tokenize on whitespace and exact-match each tag instead of substring.
      // Avoids "?vertical=use-case-pol" matching "use-case-police-and-fire".
      var tags = raw.split(/\s+/).filter(Boolean);
      var match = tags.indexOf(needle) !== -1;
      li.setAttribute('data-vertical-match', match ? 'true' : 'false');
    });

    var existing = this.section.querySelector('.ecore-samples__filters');
    if (!existing) {
      var main = this.section.querySelector('.ecore-samples__main');
      if (main) {
        var filterRow = document.createElement('div');
        filterRow.className = 'ecore-samples__filters';
        filterRow.innerHTML =
          '<button type="button" class="ecore-samples__filter is-active" data-sample-filter="vertical">Recommended for this facility</button>'
          + '<button type="button" class="ecore-samples__filter" data-sample-filter="all">Show all ECORE products</button>';
        main.insertBefore(filterRow, main.firstChild);
      }
    }
    this.urlVerticalActive = true;
  };

  SamplePicker.prototype.bind = function () {
    var self = this;
    this.section.addEventListener('click', function (e) {
      var variantBtn = e.target.closest('[data-sample-variant]');
      if (variantBtn) {
        self.toggleVariant(variantBtn);
        return;
      }
      var filterBtn = e.target.closest('[data-sample-filter]');
      if (filterBtn) {
        self.applyFilter(filterBtn.getAttribute('data-sample-filter'));
        return;
      }
      var removeBtn = e.target.closest('[data-sample-remove]');
      if (removeBtn) {
        var idx = parseInt(removeBtn.getAttribute('data-sample-remove'), 10);
        self.removeAt(idx);
        return;
      }
      var submitBtn = e.target.closest('[data-sample-submit]');
      if (submitBtn) {
        var target = submitBtn.getAttribute('data-sample-submit-target') || 'checkout';
        self.submit(target, submitBtn);
        return;
      }
      var clearAllBtn = e.target.closest('[data-sample-clear-all]');
      if (clearAllBtn) {
        self.clearAll();
        return;
      }
    });

    var inputs = this.section.querySelectorAll('[data-sample-q]');
    inputs.forEach(function (el) {
      el.addEventListener('change', function () { self.refreshUi(); self.persistAnswers(); });
      el.addEventListener('input', function () { self.refreshUi(); self.persistAnswers(); });
    });

    var otherTriggers = this.section.querySelectorAll('[data-sample-other-trigger]');
    otherTriggers.forEach(function (sel) {
      var key = sel.getAttribute('data-sample-other-trigger');
      var target = self.section.querySelector('[data-sample-other-input="' + key + '"]');
      if (!target) return;
      var input = target.querySelector('input');
      // Don't wipe the input value on init — the browser may have restored it
      // via back/forward cache. Only clear on user-driven change to a non-Other.
      var initial = true;
      var sync = function () {
        var show = sel.value === 'Other';
        target.hidden = !show;
        if (input) {
          if (show) {
            input.setAttribute('required', '');
          } else {
            input.removeAttribute('required');
            if (!initial) input.value = '';
          }
        }
        initial = false;
        self.refreshUi();
      };
      sel.addEventListener('change', sync);
      sync();
    });
  };

  SamplePicker.prototype.applyDefaultFilter = function () {
    if (this.defaultUseCase || this.urlVerticalActive) {
      this.applyFilter('vertical');
    }
  };

  SamplePicker.prototype.applyFilter = function (mode) {
    var products = this.section.querySelectorAll('.ecore-samples__product');
    products.forEach(function (li) {
      if (mode === 'vertical') {
        var match = li.getAttribute('data-vertical-match') === 'true';
        li.setAttribute('data-hidden', match ? 'false' : 'true');
      } else {
        li.setAttribute('data-hidden', 'false');
      }
    });
    var buttons = this.section.querySelectorAll('[data-sample-filter]');
    buttons.forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-sample-filter') === mode);
    });
    this.refreshFilterEmptyState();
  };

  SamplePicker.prototype.refreshFilterEmptyState = function () {
    var products = this.section.querySelectorAll('.ecore-samples__product');
    var visible = 0;
    products.forEach(function (li) {
      if (li.getAttribute('data-hidden') !== 'true') visible++;
    });
    var emptyEl = this.section.querySelector('[data-sample-filter-empty]');
    if (!emptyEl) return;
    emptyEl.hidden = visible > 0 || products.length === 0;
  };

  SamplePicker.prototype.toggleVariant = function (btn) {
    var product = btn.getAttribute('data-product-title');
    var variant = btn.getAttribute('data-variant-title');
    var added = btn.getAttribute('aria-pressed') === 'true';
    var previewing = btn.classList.contains('is-previewing');
    var swapImageUrl = btn.getAttribute('data-variant-image-url') || '';
    var card = btn.closest('.ecore-samples__card');

    // ── 1) Already-added swatch: clicking REMOVES from cart ───────────────
    if (added) {
      this.selections = this.selections.filter(function (s) {
        return !(s.productTitle === product && s.variantTitle === variant);
      });
      btn.setAttribute('aria-pressed', 'false');
      this.updateSwatchAria(btn, 'idle', product, variant);
      /* ⚠️ The swatch is the easier of the two removal paths to hit by accident - a third
         click on an added colour - so this is the one that most needs to announce itself. */
      this.showToast(product, variant, { removed: true });
      this.refreshUi();
      return;
    }

    // ── 2) Previewing swatch: SECOND click adds to cart ───────────────────
    if (previewing) {
      /* ⚠️ Fired FIRST, before the DOM work and the toast. The buzz is confirming the tap,
         so it should land with the finger rather than after a re-render. */
      haptic(50);
      btn.classList.remove('is-previewing');
      btn.setAttribute('aria-pressed', 'true');
      this.selections.push({ productTitle: product, variantTitle: variant, button: btn });
      this.updateSwatchAria(btn, 'added', product, variant);
      this.setCardHint(card, '');
      this.showToast(product, variant);
      /* ⚠️ `.ecore-samples__product`, not `card` - `card` is the inner
         `.ecore-samples__card`, and the spotlight lives on the <li> around it. */
      this.retireFocus(btn.closest('.ecore-samples__product'));
      this.pulseCount();
      this.pushDataLayer('sample_add', { product: product, variant: variant });
      this.refreshUi();
      return;
    }

    // ── 3) Idle swatch: FIRST click previews only (image swap, no add) ────
    /* ⚠️ Lighter than the add. 15ms reads as a tick rather than a confirmation - enough to
       tell you the tap registered, not enough to suggest anything was committed. */
    haptic(15);
    this.clearAllPreviews();
    btn.classList.add('is-previewing');
    this.updateSwatchAria(btn, 'previewing', product, variant);

    if (swapImageUrl && card) {
      var mainImg = card.querySelector('[data-sample-main-img]');
      if (mainImg) {
        // Clear srcset BEFORE setting src so cached high-res variants don't
        // stick. Setting srcset = '' first ensures src is what the browser
        // resolves on the next paint. (Verified across Safari/Firefox/Chrome.)
        mainImg.srcset = '';
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = swapImageUrl;
        mainImg.alt = product + ' — ' + variant;
      }
    }

    this.setCardHint(card, 'Click again to add sample');
    this.refreshUi();
  };

  SamplePicker.prototype.updateSwatchAria = function (btn, state, product, variant) {
    var hasVariant = variant && variant !== 'Default Title';
    var label;
    if (state === 'idle') {
      label = (hasVariant ? variant : 'this color') + ' — preview color';
    } else if (state === 'previewing') {
      label = (hasVariant ? variant : 'this color') + ' — click again to add to sample request';
    } else { // added
      label = (hasVariant ? variant : 'this color') + ' — added, click to remove';
    }
    btn.setAttribute('aria-label', label);
  };

  SamplePicker.prototype.clearAllPreviews = function () {
    var self = this;
    // `.ecore-samples__single-add` was named here too, until single-colour products stopped
    // rendering their own control (Ryan 2026-08-25). One selector, one card shape.
    this.section.querySelectorAll('.ecore-samples__variant.is-previewing').forEach(function (b) {
      b.classList.remove('is-previewing');
      var card = b.closest('.ecore-samples__card');
      var product = b.getAttribute('data-product-title');
      var variant = b.getAttribute('data-variant-title');
      self.updateSwatchAria(b, 'idle', product, variant);
      self.setCardHint(card, '');
    });
  };

  SamplePicker.prototype.setCardHint = function (card, text) {
    if (!card) return;
    var label = card.querySelector('.ecore-samples__variant-label');
    if (!label) return;
    if (!label.dataset.defaultText) {
      label.dataset.defaultText = label.textContent;
    }
    label.textContent = text || label.dataset.defaultText;
    label.classList.toggle('is-cta', !!text);
  };

  /* CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - ⛔ REMOVING SOMETHING SAYS SO NOW.
     *"when you click to remove a sample from your form, have a notification appear at the
     bottom with some red in it so you know you just removed something."* Adding has announced
     itself since this page was built; removing was silent, which on a two-click swatch is the
     easier of the two to do by accident - a third click on an added swatch takes it back off.

     ⚠️ ONE BUILDER, TWO MOODS, rather than a second `showRemoveToast`. The chassis, the
     timing, the stack and the teardown are identical; only the icon, its colour and one line
     of copy differ. A parallel function is how the two drift into two notification designs.
     ⚠️ "SOME red", not a red toast: the disc and a hairline edge carry it, and the dark
     chassis stays. A fully red panel reads as an error - this is a thing the user meant to do. */
  SamplePicker.prototype.showToast = function (product, variant, opts) {
    var stack = this.section.querySelector('[data-sample-toast-stack]');
    if (!stack) return;
    var removed = !!(opts && opts.removed);
    var toast = document.createElement('div');
    toast.className = 'ecore-samples__toast' + (removed ? ' ecore-samples__toast--removed' : '');
    var hasVariant = variant && variant !== 'Default Title';
    toast.innerHTML =
      '<span class="ecore-samples__toast-icon" aria-hidden="true">'
        + (removed ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>')
      + '</span>'
      + '<span class="ecore-samples__toast-text">'
        + '<strong>' + (removed ? 'Removed from your sample request' : 'Added to your sample request') + '</strong>'
        + '<span>' + escapeHtml(product) + (hasVariant ? ' — ' + escapeHtml(variant) : '') + '</span>'
      + '</span>';
    stack.appendChild(toast);
    setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 2300);
  };

  SamplePicker.prototype.pulseCount = function () {
    var countEl = this.section.querySelector('[data-sample-count]');
    if (!countEl) return;
    countEl.classList.remove('is-pulsing');
    void countEl.offsetWidth;
    countEl.classList.add('is-pulsing');
  };

  SamplePicker.prototype.pushDataLayer = function (eventName, data) {
    if (!window.dataLayer) return;
    try {
      var payload = { event: eventName };
      if (data) {
        for (var k in data) {
          if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
        }
      }
      window.dataLayer.push(payload);
    } catch (e) { /* analytics failures shouldn't break the UI */ }
  };

  // Remove every selection in one click. For cart-restored entries, also
  // POST qty=0 to /cart/change.js so the cart stays in sync. Single
  // /cart/update.js call at the end batches the cart-drawer refresh.
  SamplePicker.prototype.clearAll = function () {
    var self = this;
    if (!this.selections.length) return;
    if (!window.confirm('Remove all ' + this.selections.length + ' selected sample' + (this.selections.length === 1 ? '' : 's') + '?')) return;

    // Belt-and-suspenders swatch cleanup: query the DOM directly for ANY
    // swatch button that's currently lit (aria-pressed, data-in-cart, or
    // is-previewing) and reset it. Don't rely on this.selections alone —
    // a stale button reference would leave a lit swatch behind.
    this.section.querySelectorAll('[data-sample-variant]').forEach(function (btn) {
      var dirty = btn.getAttribute('aria-pressed') === 'true'
        || btn.getAttribute('data-in-cart') === 'true'
        || btn.classList.contains('is-previewing');
      if (!dirty) return;
      btn.setAttribute('aria-pressed', 'false');
      btn.removeAttribute('data-in-cart');
      btn.classList.remove('is-previewing');
      var p = btn.getAttribute('data-product-title');
      var v = btn.getAttribute('data-variant-title');
      self.updateSwatchAria(btn, 'idle', p, v);
      var card = btn.closest('.ecore-samples__card');
      self.setCardHint(card, '');
    });

    // Collect cart-line keys for any in-cart entries before wiping the array.
    var cartKeysToZero = this.selections
      .filter(function (s) { return s.inCart && s.cartItemKey; })
      .map(function (s) { return s.cartItemKey; });

    this.selections = [];
    this.refreshUi();

    if (cartKeysToZero.length) {
      // Sequentially zero each line — Shopify serializes cart writes anyway.
      cartKeysToZero.reduce(function (chain, key) {
        return chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        });
      }, Promise.resolve()).then(function () {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        self.markCartedSwatches();
      });
    }
  };

  SamplePicker.prototype.removeAt = function (idx) {
    var self = this;
    var sel = this.selections[idx];
    if (!sel) return;
    if (sel.button) {
      sel.button.setAttribute('aria-pressed', 'false');
      sel.button.removeAttribute('data-in-cart');
      var p = sel.button.getAttribute('data-product-title');
      var v = sel.button.getAttribute('data-variant-title');
      this.updateSwatchAria(sel.button, 'idle', p, v);
    }
    this.selections.splice(idx, 1);
    /* ⚠️ Read off the SELECTION, not off the button: an entry hydrated from the cart can
       reference a button that is no longer on the page (product filtered out, or delisted), and
       that is precisely the row somebody is most likely to be clearing. */
    this.showToast(sel.productTitle, sel.variantTitle, { removed: true });
    this.refreshUi();
    // If this entry was hydrated from the cart, also remove the line
    // from the cart so the sidebar + cart stay in sync. Fire-and-forget;
    // refresh the cart drawer so the header count updates.
    if (sel.inCart && sel.cartItemKey) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: sel.cartItemKey, quantity: 0 })
      }).then(function () {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        // Recompute cartHasSamples + selection list state from fresh cart.
        self.markCartedSwatches();
      });
    }
  };

  SamplePicker.prototype.refreshUi = function () {
    var self = this;
    var count = this.selections.length;
    var countEl = this.section.querySelector('[data-sample-count]');
    if (countEl) {
      countEl.textContent = count + (count === 1 ? ' sample selected' : ' samples selected');
    }

    var listEl = this.section.querySelector('[data-sample-selected]');
    if (listEl) {
      if (count === 0) {
        listEl.innerHTML = '<li class="ecore-samples__empty-list" data-sample-empty>Pick a color from any product to start.</li>';
      } else {
        var html = this.selections.map(function (s, i) {
          return '<li>'
            + '<span><strong>' + escapeHtml(s.productTitle) + '</strong>' + (s.variantTitle && s.variantTitle !== 'Default Title' ? ' — ' + escapeHtml(s.variantTitle) : '') + '</span>'
            + '<button type="button" class="ecore-samples__remove" data-sample-remove="' + i + '" aria-label="Remove this sample">×</button>'
            + '</li>';
        }).join('');
        listEl.innerHTML = html;
      }
    }

    var allRequiredMet = this.requiredQuestionsMet();
    var hasNewSelections = count > 0 && allRequiredMet;
    this.section.querySelectorAll('[data-sample-submit]').forEach(function (btn) {
      var target = btn.getAttribute('data-sample-submit-target');
      var enabled;
      if (target === 'checkout') {
        // Checkout is enabled if there's a new request to add OR the cart
        // already has samples (user can checkout existing cart without
        // adding more).
        enabled = hasNewSelections || self.cartHasSamples;
        // Label: when only cart has samples (no new sidebar selections),
        // show the count so the user knows what they're about to check out.
        var def = btn.querySelector('.ecore-samples__submit-default');
        if (def && !btn.classList.contains('is-success')) {
          if (count === 0 && self.cartHasSamples) {
            def.textContent = 'Checkout (' + self.cartSampleCount + ' in cart)';
          } else {
            def.textContent = 'Continue to Checkout';
          }
        }
      } else {
        // Add to Cart only makes sense when there's something new to add.
        enabled = hasNewSelections;
      }
      btn.disabled = !enabled || self.submitting;
    });

    // Show Clear all button only when there's something to clear.
    var clearBtn = this.section.querySelector('[data-sample-clear-all]');
    if (clearBtn) clearBtn.hidden = count === 0;
  };

  SamplePicker.prototype.requiredQuestionsMet = function () {
    var inputs = this.section.querySelectorAll('[data-sample-q][required]');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].value) return false;
    }
    return true;
  };

  SamplePicker.prototype.submit = function (target, clickedBtn) {
    var self = this;
    // Double-submit guard: a fast double-click could fire `submit` twice
    // before the disabled state propagates. The flag short-circuits the
    // 2nd entry instantly.
    if (this.submitting) return;

    // Empty-sidebar + has-cart-samples + checkout target: just navigate.
    // The user has nothing new to add but wants to check out their
    // existing cart. Skip the cart-add + questionnaire path entirely.
    if (!this.selections.length) {
      if (target === 'checkout' && this.cartHasSamples) {
        window.location.href = '/checkout';
      }
      return;
    }
    if (!this.requiredQuestionsMet()) return;

    this.submitting = true;
    this.activeTarget = target;
    this.activeBtn = clickedBtn || null;

    var errorEl = this.section.querySelector('[data-sample-error]');

    // Only the CLICKED button disables + shows the "Adding..." loading
    // label. Greying out the other button looked like it was also being
    // submitted. Double-submit is prevented by the this.submitting flag
    // at the top of submit(), not by visually disabling the other btn.
    if (clickedBtn) {
      clickedBtn.disabled = true;
      var def = clickedBtn.querySelector('.ecore-samples__submit-default');
      var load = clickedBtn.querySelector('.ecore-samples__submit-loading');
      if (def) def.hidden = true;
      if (load) load.hidden = false;
    }
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    // Build cart attributes from the questionnaire — applies to the whole
    // sample request, not per-line-item. Visible in order admin + emails.
    var cartAttributes = {};
    var primaryInputs = this.section.querySelectorAll('[data-sample-q]:not([data-sample-q$="— Other"])');
    primaryInputs.forEach(function (el) {
      var key = el.getAttribute('data-sample-q');
      var val = el.value;
      if (!key || !val) return;
      if (val === 'Other') {
        var triggerKey = el.getAttribute('data-sample-other-trigger');
        if (triggerKey) {
          var otherWrap = self.section.querySelector('[data-sample-other-input="' + triggerKey + '"]');
          var otherInput = otherWrap ? otherWrap.querySelector('input') : null;
          if (otherInput && otherInput.value) {
            cartAttributes[key] = 'Other — ' + otherInput.value;
            return;
          }
        }
      }
      cartAttributes[key] = val;
    });

    // CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-07-08) — attach the visitor's UTM /
    // click-ID journey so the sample-request LEAD (created server-side by the
    // orders/create webhook, which never sees the browser) can attribute to a paid
    // channel like every form lead. Rides in as a cart attribute → order
    // note_attributes → webhook → createLead's computeAttribution. Strip the bulky
    // referrer/landingPath fields and cap the serialized size (drop oldest touches)
    // so it stays comfortably within Shopify's note-attribute limits.
    try {
      if (window.BSFSJourney && typeof window.BSFSJourney.read === 'function') {
        var _jrny = (window.BSFSJourney.read() || []).map(function (t) {
          var o = {};
          Object.keys(t || {}).forEach(function (k) { if (k !== 'referrer' && k !== 'landingPath') o[k] = t[k]; });
          return o;
        });
        while (_jrny.length > 1 && JSON.stringify(_jrny).length > 4000) _jrny.shift();
        if (_jrny.length) cartAttributes.bsfs_utm_journey = JSON.stringify(_jrny);
      }
    } catch (e) {}

    // Build one line item per selection. Same sample variant on all of
    // them, but each carries a unique "Sample" property — Shopify keeps
    // line items separate when their properties differ, so the cart
    // shows N rows, one per requested swatch.
    var itemsToAdd = this.selections.map(function (s) {
      var sampleLabel = s.productTitle;
      if (s.variantTitle && s.variantTitle !== 'Default Title') {
        sampleLabel += ' — ' + s.variantTitle;
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-15) — Carry the swatch's
         product identity through to the order so the rep notification email
         can show a real thumbnail + a link to the PDP instead of a bare name
         string. Everything needed is already on the DOM: the swatch button
         holds the variant image, and its ancestor product card holds the
         handle. Previously all of it was thrown away here.

         The `_` prefix is load-bearing: Shopify hides underscore-prefixed
         line-item properties from the cart drawer AND the customer's order
         confirmation, so the customer never sees a CDN URL — but they still
         ship in the orders/create webhook payload, which is where
         handlers/webhooks.js reads them.

         ⚠ Do NOT fold these into the `Sample` label. The cart dedup below and
         the hydration path in bindCartState both match on that exact string;
         changing its shape silently breaks re-add detection. */
      var handle = '';
      var swatch = '';
      if (s.button) {
        swatch = s.button.getAttribute('data-variant-image-url') || '';
        // Null-guard: a selection rebuilt from the cart can reference a button
        // that is no longer on the page (product filtered out, or delisted).
        var card = s.button.closest('.ecore-samples__product');
        if (card) handle = card.getAttribute('data-handle') || '';
      }

      var props = { Sample: sampleLabel };
      if (handle) props._SampleHandle = handle;
      if (swatch) props._SampleSwatch = swatch;

      return {
        id: self.variantId,
        quantity: 1,
        properties: props
      };
    });

    // ADDITIVE flow: don't wipe existing sample lines from the cart.
    // Users frequently add some samples, navigate away, come back, and add
    // more — the prior selections must persist. To avoid duplicates when
    // a user re-adds the same swatch, fetch the current cart and filter
    // out itemsToAdd whose Sample property already matches an existing
    // line. (Shopify's natural merging would otherwise bump qty to 2,
    // which doesn't make sense for sample requests.)
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var existingSampleLabels = (cart.items || [])
          .filter(function (item) { return item.variant_id === self.variantId; })
          .map(function (item) { return item.properties && item.properties.Sample; })
          .filter(Boolean);
        var deduped = itemsToAdd.filter(function (it) {
          return existingSampleLabels.indexOf(it.properties.Sample) === -1;
        });
        if (deduped.length === 0) {
          // Nothing new to add (everything is already in cart). Treat as
          // success so the UI shows the green ✓ confirmation.
          return Promise.resolve(new Response(JSON.stringify({ items: [] }), { status: 200, headers: { 'Content-Type': 'application/json' } }));
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: deduped })
        });
      })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add samples to cart.';
          throw new Error(msg);
        }
        // Set cart-level attributes for the questionnaire (use case, role,
        // etc.). Per-line-item duplication would clutter the order admin —
        // these answers describe the whole request, not each sample.
        if (Object.keys(cartAttributes).length > 0) {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: cartAttributes })
          }).then(function (res) {
            return { ok: res.ok };
          });
        }
        return { ok: true };
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add samples to cart.';
          throw new Error(msg);
        }
        self.pushDataLayer('sample_submit', { count: self.selections.length, target: self.activeTarget });
        if (self.activeTarget === 'checkout') {
          window.location.href = '/checkout';
          return;
        }
        // Add to Cart path: tell the theme's cart drawer + header icon to
        // re-fetch and render. Without this, the cart count badge stays
        // stale and clicking the cart icon shows the pre-add contents
        // until the user manually refreshes.
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        // Re-mark in-cart swatches so the just-added ones get their green
        // ✓ indicator immediately, and Continue to Checkout enables.
        self.markCartedSwatches();
        // Add to Cart: stay on the page so the user can keep adding samples.
        // Show a transient "Added!" state on the clicked button, then reset.
        var btn = self.activeBtn;
        if (btn) {
          var def = btn.querySelector('.ecore-samples__submit-default');
          var load = btn.querySelector('.ecore-samples__submit-loading');
          if (load) load.hidden = true;
          if (def) {
            if (!btn.dataset.defaultLabel) btn.dataset.defaultLabel = def.textContent;
            def.textContent = '✓ Added to cart';
            def.hidden = false;
          }
          btn.classList.add('is-success');
        }
        setTimeout(function () {
          if (btn) {
            var def2 = btn.querySelector('.ecore-samples__submit-default');
            if (def2 && btn.dataset.defaultLabel) def2.textContent = btn.dataset.defaultLabel;
            btn.classList.remove('is-success');
          }
          self.resetSubmitState();
        }, 1800);
      })
      .catch(function (err) {
        self.resetSubmitState();
        if (errorEl) {
          errorEl.textContent = err.message || 'Something went wrong. Please try again or contact us.';
          errorEl.hidden = false;
        }
      });
  };

  // Centralized state-reset for both submit buttons. Used after a failed
  // submit AND after BFCache restore (pageshow with persisted=true) so
  // returning from /checkout via Back doesn't leave the buttons stuck in
  // their loading/disabled state.
  SamplePicker.prototype.resetSubmitState = function () {
    this.submitting = false;
    var self = this;
    this.section.querySelectorAll('[data-sample-submit]').forEach(function (btn) {
      var def = btn.querySelector('.ecore-samples__submit-default');
      var load = btn.querySelector('.ecore-samples__submit-loading');
      if (def) def.hidden = false;
      if (load) load.hidden = true;
    });
    self.refreshUi();
  };

  // Escape ALL HTML-context-significant chars including quotes (defense in depth
  // — current call sites use textContent paths but staff-engineer-tier code
  // shouldn't ship an "html escape" helper that doesn't escape quotes).
  /* ============================================
     haptic - a tap you can feel, on the phones that can produce one
     ============================================
     CUSTOM_ECORE_BY_CLAUDE (Ryan 2026-08-25) - *"50 ms haptics when selecting swatches on
     mobile. actually, less for first click, then 50 for 2nd click which adds it officially."*

     ⛔ THE TWO CLICKS FEEL DIFFERENT BECAUSE THEY ARE DIFFERENT. The first previews and
     commits nothing; the second is what actually puts a sample in the request. A single
     weight on both would say they are the same act, which is the one thing this two-click
     control needs the user NOT to think.

     ⚠️ 50ms MATCHES THE REST OF THE SITE. `bb-hotspot.js`, `custom-chatbot.js` and
     `custom-designer-kb.js` all buzz 50 on a confirm, so an added swatch feels like every
     other committed tap rather than like its own invention.

     ⚠️ iOS SAFARI DOES NOT IMPLEMENT THE VIBRATION API AT ALL - not "badly", not "needs a
     permission": `navigator.vibrate` is absent. This is an Android-only nicety and the guard
     is what stops it being an iPhone-only crash. Desktop browsers that expose the API without
     hardware simply no-op, so nothing needs a viewport check.
     ⚠️ Wrapped in try/catch as well as guarded: a browser may expose `vibrate` and still
     throw when the document has never been interacted with, and a decorative buzz must never
     be the thing that stops a swatch being added. */
  function haptic(ms) {
    try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) { /* no motor, no matter */ }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function init() {
    document.querySelectorAll('[data-ecore-samples]').forEach(function (sec) {
      if (sec.__ecoreSamplesInit) return;
      sec.__ecoreSamplesInit = true;
      new SamplePicker(sec);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
